import type { Breadcrumb, SeverityLevel } from '@sentry/react'
import {
  getCurrentScope,
  addBreadcrumb as sentryAddBreadcrumb,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  withScope,
} from '@sentry/react'

import config from '@app/config'

import { getCurrentStackPlace } from '@app/utils/getCurrentStackPlace'

import type { StoreProfileUser } from '@app/store/types/profile'

export type Severity = SeverityLevel

interface CaptureParams {
  /** data must be JSON serializable */
  extra?: { [key: string]: any }
  tags?: { [key: string]: string }
  level?: Severity
  silent?: boolean
}

export function captureMessage(message: string, params?: CaptureParams) {
  if (config.sentry) {
    withScope(scope => {
      if (params) {
        if (params.extra) {
          for (const [key, value] of Object.entries(params.extra)) {
            scope.setExtra(key, value)
          }
        }
        if (params.tags) {
          for (const [key, value] of Object.entries(params.tags)) {
            scope.setTag(key, value)
          }
        }
        if (params.level) {
          scope.setLevel(params.level)
        }
      }
      sentryCaptureMessage(message)
    })
  } else {
    console.warn('[Capture message]', getCurrentStackPlace(1))
    console.warn(message)
  }
}

/**
 * Captures exceptions and sends to Sentry. Client only.
 *
 * @param err error string
 */
export function captureException(err: any, params?: CaptureParams) {
  if (config.sentry) {
    withScope(scope => {
      if (params) {
        if (params.extra) {
          for (const [key, value] of Object.entries(params.extra)) {
            scope.setExtra(key, value)
          }
        }
        if (params.tags) {
          for (const [key, value] of Object.entries(params.tags)) {
            scope.setTag(key, value)
          }
        }
        if (params.level) {
          scope.setLevel(params.level)
        }
      }
      sentryCaptureException(err)
    })
  } else {
    console.error('[Capture exception]', getCurrentStackPlace(1))
    console.error(err)
  }
  if (params && !params.silent) console?.error?.(err, params)
}

export function addBreadcrumb(breadcrumb: Breadcrumb) {
  if (config.sentry) {
    sentryAddBreadcrumb(breadcrumb)
  }
}

export function identify(user: StoreProfileUser, additionalData?: { [key: string]: any }) {
  const data = { ...additionalData }
  if (user) {
    data.id = user.id
    if ('token' in user) data.token = user.token
  }
  getCurrentScope().setUser(data)
}
